import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText,
  CheckCircle,
  Clock,
  XCircle,
  Users,
  RefreshCw,
  ChevronDown,
  X,
} from 'lucide-react';
import { dashboardApi } from '../api/dashboardApi';
import { License } from '../types/License';
import './OverviewPage.css';

interface DashboardStats {
  total_licenses: number;
  active_licenses: number;
  expired_licenses: number;
  expiring_soon: number;
  unique_users: number;
}

const displayOrg = (org?: string) => org?.trim() || '—';

const formatExpiry = (value?: string): string => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const daysLabel = (days: number): string => {
  if (days <= 0) return 'Expired';
  if (days === 1) return '1 day';
  return `${days} days`;
};

const daysClass = (days: number): string => {
  if (days <= 1) return 'ov-days ov-days--urgent';
  if (days <= 3) return 'ov-days ov-days--warn';
  return 'ov-days';
};

const OverviewPage: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expiringOpen, setExpiringOpen] = useState(false);
  const [expiringLicenses, setExpiringLicenses] = useState<License[]>([]);
  const [expiringLoading, setExpiringLoading] = useState(false);
  const [expiringFetched, setExpiringFetched] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await dashboardApi.getStats();
      setStats({
        total_licenses: data.total_licenses,
        active_licenses: data.active_licenses,
        expired_licenses: data.expired_licenses,
        expiring_soon: data.expiring_soon,
        unique_users: data.unique_users,
      });
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      setError('Failed to load overview');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchExpiringLicenses = useCallback(async () => {
    try {
      setExpiringLoading(true);
      const data = await dashboardApi.getLicenses({
        page: 1,
        limit: 50,
        status_filter: 'expiring_soon',
        sort_by: 'expires_at',
        sort_order: 'asc',
      });
      const sorted = [...data.licenses].sort(
        (a, b) => (a.days_remaining ?? 999) - (b.days_remaining ?? 999)
      );
      setExpiringLicenses(sorted);
      setExpiringFetched(true);
    } catch (err) {
      console.error('Failed to fetch expiring licenses:', err);
    } finally {
      setExpiringLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const toggleExpiringPanel = () => {
    const next = !expiringOpen;
    setExpiringOpen(next);
    if (next && !expiringFetched) {
      fetchExpiringLicenses();
    }
  };

  const closeExpiringPanel = () => setExpiringOpen(false);

  if (loading) {
    if (embedded) {
      return (
        <div className="ov-page ov-page--embedded">
          <div className="ov-stats ov-stats--loading">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="ov-stat ov-stat--static ov-stat--skeleton" />
            ))}
          </div>
        </div>
      );
    }
    return (
      <div className="ov-page ov-page-loading">
        <RefreshCw size={20} className="animate-spin" style={{ marginBottom: 8 }} />
        <div>Loading overview…</div>
      </div>
    );
  }

  if (error) {
    if (embedded) {
      return (
        <div className="ov-page ov-page--embedded ov-page-error-inline">
          <p>{error}</p>
          <button type="button" className="ov-retry" onClick={fetchStats}>
            Try again
          </button>
        </div>
      );
    }
    return (
      <div className="ov-page ov-page-error">
        <h3>Could not load overview</h3>
        <p>{error}</p>
        <button type="button" className="ov-retry" onClick={fetchStats}>
          Try again
        </button>
      </div>
    );
  }

  const expiringCount = stats?.expiring_soon ?? 0;

  return (
    <div className={`ov-page${embedded ? ' ov-page--embedded' : ''}`}>
      <div className="ov-stats">
        <div className="ov-stat ov-stat--static">
          <div className="ov-stat-head">
            <div className="ov-stat-icon ov-stat-icon--blue">
              <FileText size={15} />
            </div>
          </div>
          <div className="ov-stat-value">{stats?.total_licenses ?? 0}</div>
          <div className="ov-stat-label">Total licenses</div>
        </div>

        <div className="ov-stat ov-stat--static">
          <div className="ov-stat-head">
            <div className="ov-stat-icon ov-stat-icon--green">
              <CheckCircle size={15} />
            </div>
          </div>
          <div className="ov-stat-value">{stats?.active_licenses ?? 0}</div>
          <div className="ov-stat-label">Active</div>
        </div>

        <button
          type="button"
          className={`ov-stat ov-stat--clickable ${expiringOpen ? 'ov-stat--active' : ''}`}
          onClick={toggleExpiringPanel}
          aria-expanded={expiringOpen}
        >
          <div className="ov-stat-head">
            <div className="ov-stat-icon ov-stat-icon--amber">
              <Clock size={15} />
            </div>
            <ChevronDown
              size={14}
              color="#94a3b8"
              style={{
                transform: expiringOpen ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.15s',
              }}
            />
          </div>
          <div className="ov-stat-value">{expiringCount}</div>
          <div className="ov-stat-label">Expiring soon</div>
          <div className="ov-stat-hint">Next 7 days · click to view</div>
        </button>

        <div className="ov-stat ov-stat--static">
          <div className="ov-stat-head">
            <div className="ov-stat-icon ov-stat-icon--red">
              <XCircle size={15} />
            </div>
          </div>
          <div className="ov-stat-value">{stats?.expired_licenses ?? 0}</div>
          <div className="ov-stat-label">Expired</div>
        </div>

        <div className="ov-stat ov-stat--static">
          <div className="ov-stat-head">
            <div className="ov-stat-icon ov-stat-icon--slate">
              <Users size={15} />
            </div>
          </div>
          <div className="ov-stat-value">{stats?.unique_users ?? 0}</div>
          <div className="ov-stat-label">Unique users</div>
        </div>
      </div>

      {expiringOpen && (
        <div className="ov-panel">
          <div className="ov-panel-header">
            <div>
              <h3 className="ov-panel-title">Licenses expiring within 7 days</h3>
              <p className="ov-panel-sub">
                {expiringCount === 0
                  ? 'No licenses are due to expire soon'
                  : `${expiringCount} license${expiringCount === 1 ? '' : 's'} need attention`}
              </p>
            </div>
            <button type="button" className="ov-panel-close" onClick={closeExpiringPanel} aria-label="Close">
              <X size={14} />
            </button>
          </div>

          <div className="ov-panel-body">
            {expiringLoading ? (
              <div className="ov-panel-loading">
                <RefreshCw size={18} className="animate-spin" />
              </div>
            ) : expiringLicenses.length === 0 ? (
              <div className="ov-empty">No licenses expiring in the next 7 days.</div>
            ) : (
              <table className="ov-table">
                <thead>
                  <tr>
                    <th>Organization</th>
                    <th>Expires</th>
                    <th>Remaining</th>
                  </tr>
                </thead>
                <tbody>
                  {expiringLicenses.map((license) => (
                    <tr key={license.id}>
                      <td>
                        <span className="ov-org-name">{displayOrg(license.org_name)}</span>
                        <span className="ov-org-email">{license.email}</span>
                      </td>
                      <td>{formatExpiry(license.expires_at)}</td>
                      <td>
                        <span className={daysClass(license.days_remaining ?? 0)}>
                          {daysLabel(license.days_remaining ?? 0)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default OverviewPage;
